import { Plus, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { AddMenuForm } from "../components/menus/AddMenuForm";
import { sidebarService } from "../api/services/sidebar/sidebarService";
import { toast } from "react-hot-toast";
import { useAllMenus } from "../hooks/useAllMenus";
import { SidebarMenu } from "../api/services/auth/type";

export default function MenuBar() {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingMenu, setEditingMenu] = useState<SidebarMenu | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [menuToDelete, setMenuToDelete] = useState<string | null>(null);
  const { refetch: refetchMenus, data: menus, isLoading } = useAllMenus();

  const handleCreateMenu = () => {
    setEditingMenu(null);
    setShowForm(true);
  };

  const handleEditMenu = (menu: SidebarMenu) => {
    setEditingMenu(menu);
    setShowForm(true);
  };

  const handleDeleteMenu = (id: string) => {
    setMenuToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!menuToDelete) return;

    try {
      await sidebarService.deleteMenu(menuToDelete);
      await refetchMenus();
      toast.success("Menu deleted successfully!");
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to delete menu";
      toast.error(message);
    } finally {
      setDeleteDialogOpen(false);
      setMenuToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setMenuToDelete(null);
  };
  const handleMenuSubmit = async (data: {
    menu_name: string;
    route_path: string;
  }) => {
    setIsSubmitting(true);
    try {
      if (editingMenu) {
        await sidebarService.updateMenu(editingMenu.id, data);
        toast.success("Menu updated successfully!");
      } else {
        await sidebarService.createMenu(data);
        toast.success("Menu created successfully!");
      }
      await refetchMenus();
      setShowForm(false);
      setEditingMenu(null);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "An error occurred";
      toast.error(
        `Failed to ${editingMenu ? "update" : "create"} menu: ${message}`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleAddMenuCancel = () => {
    setShowForm(false);
  };
  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="bg-slate-800 rounded-lg p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleCreateMenu}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium whitespace-nowrap"
          >
            <Plus size={20} />
            Add Menu
          </button>
        </div>
      </div>
      <div className="bg-slate-800 rounded-lg p-6">
        <h1 className="text-2xl font-bold text-white mb-6">Menu List</h1>
        {isLoading ? (
          <div className="flex justify-center items-center h-32">
            <p className="text-slate-400">Loading menus...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">
                    Menu Name
                  </th>
                  <th className="text-left py-3 px-4 text-slate-400 font-medium">
                    Route Path
                  </th>
                  <th className="text-right py-3 px-4 text-slate-400 font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {menus?.map((menu) => (
                  <tr
                    key={menu.id}
                    className="border-b border-slate-700 hover:bg-slate-700/50"
                  >
                    <td className="py-3 px-4 text-slate-200">
                      {menu.menu_name}
                    </td>
                    <td className="py-3 px-4 text-slate-400">
                      {menu.route_path}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEditMenu(menu)}
                          className="text-blue-400 hover:text-blue-300 p-1.5 rounded hover:bg-slate-700 transition-colors"
                          title="Edit menu"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteMenu(menu.id)}
                          className="text-red-400 hover:text-red-300 p-1.5 rounded hover:bg-slate-700 transition-colors"
                          title="Delete menu"
                          disabled={isSubmitting}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showForm && (
        <AddMenuForm
          initialData={editingMenu}
          isLoading={isSubmitting}
          onSubmit={handleMenuSubmit}
          onCancel={handleAddMenuCancel}
        />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Menu"
        description="Are you sure you want to delete this menu item? This action cannot be undone."
        icon={<Trash2 className="h-8 w-8 text-white" />}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}
